import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { useMe } from '@/api/queries/auth-queries'
import { AccessDeniedState } from '@/components/commons'
import { NoticeBanner, NoticeBlockingDialog } from '@/components/notices'
import { IosInstallGuide, PwaInstallBanner } from '@/components/pwa-install'
import { hasRouteAccess } from '@/constants/permissions'
import { ROUTES } from '@/constants/routes'
import { usePushListener } from '@/hooks/use-push-notifications'
import { useAuthStore } from '@/store'
import { Header } from '../header'

export const PrivateLayout = () => {
	const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
	const user = useAuthStore((s) => s.user)
	const updateUser = useAuthStore((s) => s.updateUser)
	const logout = useAuthStore((s) => s.logout)
	const location = useLocation()

	usePushListener()

	// Always validate the session on mount via /auth/me
	const { data: profile, isLoading, isError } = useMe(isAuthenticated)

	// Sync fetched profile into the store
	useEffect(() => {
		if (profile) {
			updateUser(profile)
		}
	}, [profile, updateUser])

	// Cookie expired or invalid — clear store and redirect to login
	useEffect(() => {
		if (isError) {
			logout()
		}
	}, [isError, logout])

	if (!isAuthenticated || isError) {
		// Guarda o destino para voltar após o login (ex.: link do QR de presença)
		return (
			<Navigate
				replace
				state={{ from: `${location.pathname}${location.search}` }}
				to={ROUTES.PUBLIC.LOGIN}
			/>
		)
	}

	// Wait for user profile before rendering
	if (!user && isLoading) {
		return (
			<div className="flex h-dvh items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-kaefer-medium-grey" />
			</div>
		)
	}

	// If user must change password, redirect to force-change
	if (user?.shouldChangePassword) {
		return <Navigate replace to={ROUTES.PUBLIC.FORCE_CHANGE_PASSWORD} />
	}

	// Role-based access check
	if (user && !hasRouteAccess(user.role, location.pathname, user.badge)) {
		return <AccessDeniedState />
	}

	return (
		// use full viewport height and allow main content to stretch
		<div className="flex h-dvh w-full flex-col">
			<Header />
			<PwaInstallBanner />
			<IosInstallGuide />
			{/* Avisos de prioridade média só na home; os de prioridade alta bloqueiam em qualquer tela */}
			{location.pathname === ROUTES.DASHBOARD.HOME && <NoticeBanner />}
			<NoticeBlockingDialog />
			<main className="flex w-full min-w-[320px] flex-1 flex-col space-y-4 bg-background">
				<Outlet />
			</main>
		</div>
	)
}
